import { forwardRef, Inject, Injectable } from '@angular/core';
import { LegacyUibModalService } from '../modals/legacy-uib-modal.service';
import { TranslateService } from '@ngx-translate/core';

import { API }              from '../api/api.constant';
import { ApiService }       from '../api/api.service';
import { CompanyService }   from '../companies/company.service';
import { ConfirmService }   from '../messages/confirm/confirm.service';
import { MessengerService } from '../messages/messenger/messenger.service';

import { AddCustomerFromBranchComponent }   from './customer.add-from-branch.component';
import { CustomerBranchHistoryComponent } from './customer.branch-history.component';
import { InvoicesForPaymentComponent }    from '../invoices/invoices.for-payment.component';
import ChangeCustomerHtml               from './customer.change.html';
import CustomerStatementSettingsHtml    from './customer.statement-settings.html';
import EmailCustomerStatementHtml       from './customer.email-statement.html';
import ResolveCustomerMatchesHtml       from './customer.resolve-matches.html';
import SendCustomerPortalInvitationHtml from './customer.send-portal-invitation.html';
import WindowFlipHtml                   from '../core/templates/window-flip.html';

@Injectable({ providedIn: 'root' })
export class CustomerFactoryService {
  customer: any;

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => CompanyService))
    private companyService: CompanyService,
    @Inject(forwardRef(() => ConfirmService)) private confirmService: ConfirmService,
    @Inject(forwardRef(() => MessengerService)) private messengerService: MessengerService,
    private modalService: LegacyUibModalService,
    private translateService: TranslateService
  ) {}

  create(customer?: any): CustomerFactoryService {
    this.customer = customer || {};
    return this;
  }

  setCustomer(customer: any): CustomerFactoryService {
    this.customer = customer;
    return this;
  }

  change(): Promise<any> {
    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.modalService.open({
      controller : 'ChangeCustomerCtrl',
      keyboard   : false,
      templateUrl: ChangeCustomerHtml,
      resolve    : { customer: () => this.customer }
    }).result;
  }

  confirmPortalInvitation(sendAsSms = false): Promise<any> {
    if (sendAsSms && !this.customer.mobile) {
      return Promise.reject(this.translateService.instant('CUSTOMER_SPACE.CUSTOMER_PORTAL.SMS_NEEDED'));
    }

    return this.apiService.post('/customers/send_customer_portal_invitation', {
      customer_id: this.customer.id,
      sms        : sendAsSms
    });
  }

  copyFromBranch(customer: any): Promise<any> {
    return this.apiService.get('/customers/copy_from_branch',
      customer.vehicle_id ? [customer.id, customer.vehicle_id] : customer.id
    );
  }

  createCustomerFromOtherBranch(quickAdd?: any): Promise<any> {
    const modalRef = this.modalService.open(AddCustomerFromBranchComponent, {
      windowClass: 'x-lg-modal',
      keyboard   : false
    });
    modalRef.componentInstance.customerQuickAdd = quickAdd;
    return modalRef.result.catch(() => {});
  }

  delete(): Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_CUSTOMER'))
      .then(() => this.apiService.delete('/customer', this.customer.id));
  }

  email(email?: string, body?: string): Promise<any> {
    return this.messengerService.email({
      attachment_type: 4,
      body           : body,
      email_address  : email || this.customer.email,
      name           : this.customer.display_name
    });
  }

  sms(customer = this.customer): Promise<any> {
    return this.messengerService.sms({
      name   : customer.display_name,
      send_to: customer.mobile
    });
  }

  emailStatement(): Promise<any> {
    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.modalService.open({
      controller       : 'EmailCustomerStatementCtrl',
      templateUrl      : EmailCustomerStatementHtml,
      windowClass      : 'flip-container',
      windowTemplateUrl: WindowFlipHtml,
      resolve          : {
        companyMessages: () => this.companyService.getDefaultMessages(),
        customer       : () => this.customer
      }
    }).result;
  }

  findMatchingCustomers(customer: any): Promise<any> {
    return this.apiService.get('/branch_matching/find_matches_for_customer', customer.id);
  }

  getBranchCustomerInvoices(): Promise<any> {
    return this.apiService.get('/customers/branch_invoice_count', [this.customer.id, 'P', 'I'])
      .then(count => this.apiService.get('/customers/branch_invoice_list', [
        this.customer.id, 0, count, 'P', 'I', 'post_date', 'desc'
      ]));
  }

  getBranchCustomers(filters: any): Promise<any> {
    return this.apiService.get('/customers/branch_find', [
      filters.search_type, filters.search_term,
      filters.offset, filters.pageSize,
      filters.field, filters.direction
    ], null, { skipEncoding: true });
  }

  hasPaymentTerm(): boolean {
    return this.customer?.default_payment_terms
      && this.customer.default_payment_terms !== 'NONE';
  }

  isBiller(): boolean {
    return this.customer?.biller;
  }

  isBranchCustomer(customer = this.customer): boolean {
    return customer?.is_branch_customer;
  }

  isLinkedToHeadOffice(customer = this.customer): boolean {
    return !!customer?.branch_link;
  }

  isType(type = 'company'): boolean {
    switch (type) {
      case 'individual': return this.customer?.customer_type === 'I';
      case 'company': default: return this.customer?.customer_type === 'C';
    }
  }

  lookupBranchCustomerInvoices(): Promise<any> {
    return this.getBranchCustomerInvoices().then(invoices => {
      const modalRef = this.modalService.open(InvoicesForPaymentComponent, { windowClass: 'x-lg-modal' });
      modalRef.componentInstance.customer = this.customer;
      modalRef.componentInstance.invoices = invoices;
      return modalRef.result;
    }).catch(() => {});
  }

  printStatement(): Promise<any> {
    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.modalService.open({
      controller : 'CustomerStatementSettingsCtrl',
      templateUrl: CustomerStatementSettingsHtml,
      windowClass: 'md-modal'
    }).result.then(settings => {
      if (settings.statement) {
        window.open(`${API.url}/reports/jasper_statements_306090/${this.customer.id}/${settings.as_at}.pdf`, '_blank');
      } else {
        window.open(`${API.url}/reports/jasper_statements/${this.customer.id}/${settings.begin_date}/${settings.end_date}/${settings.include_paid}.pdf`, '_blank');
      }
    });
  }

  resolveMatches(customer: any, matches: any): Promise<any> {
    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.modalService.open({
      controller : 'ResolveCustomerMatchesCtrl',
      keyboard   : false,
      resolve    : {
        customer: () => customer,
        matches : () => matches
      },
      templateUrl: ResolveCustomerMatchesHtml,
      windowClass: 'x-lg-modal'
    }).result.catch(() => customer);
  }

  save(settings: any = {}): Promise<any> {
    const method = this.customer.id ? 'patch' : 'post';
    const url    = this.customer.id ? '/customer' : '/customers';

    return this.apiService[method](url, this.customer, 'customer', null, settings)
      .then(customer => {
        if (!this.customer.id
          && this.companyService.isSharedAccounting()
          && this.companyService.isAccountingSetup()
        ) {
          return this.findMatchingCustomers(customer)
            .then(matches => matches.length
              ? this.resolveMatches(customer, matches).then(() => customer)
              : customer
            );
        }
        return customer;
      });
  }

  saveMatch(match: any): Promise<any> {
    return this.apiService.post('/branch_matching/link_customer_ids', [
      { id: this.customer.id, accounting_id: match.accounting_id }
    ], 'links');
  }

  sendPortalInvitation(): Promise<any> {
    if (!this.customer.email) {
      return Promise.reject(this.translateService.instant('CUSTOMER_SPACE.CUSTOMER_PORTAL.EMAIL_NEEDED'));
    }

    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.modalService.open({
      controller : 'SendCustomerPortalInvitationCtrl',
      resolve    : { customer: () => this.customer },
      templateUrl: SendCustomerPortalInvitationHtml
    }).result;
  }

  validateCompanyName(name: string): Promise<boolean> {
    return this.apiService.post('/customers/verify_company_name_uniqueness', { company_name: name })
      .then(result => result.company_name_unique);
  }

  validateFirstAndLastName(name: string, field = 'first_name'): Promise<boolean> {
    if (field === 'first_name' && (!name || !this.customer.last_name)) {
      return Promise.resolve(true);
    }
    if (field === 'last_name' && (!name || !this.customer.first_name)) {
      return Promise.resolve(true);
    }

    return this.apiService.post('/customers/verify_name_uniqueness', {
      first_name: field === 'first_name' ? name : this.customer.first_name,
      last_name : field === 'last_name' ? name : this.customer.last_name,
    }).then(result => result.name_unique);
  }

  viewBranchHistory(customer: any): Promise<any> {
    return this.apiService.get('/customers/invoice_list', [
      customer.id, 0, '*', '*', '*', 'post_date', 'desc'
    ]).then(invoices => {
      const modalRef = this.modalService.open(CustomerBranchHistoryComponent, { windowClass: 'x-lg-modal' });
      modalRef.componentInstance.customer = customer;
      modalRef.componentInstance.invoices = invoices;
      return modalRef.result.catch(() => {});
    });
  }
}
